import React, { useState, useEffect } from 'react';
import { Elements, CardElement, useStripe, useElements, PaymentRequestButtonElement, AddressElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';

const stripePromise = loadStripe('pk_live_51PRDa3ITs3jl5ALXZDPxAs6NGLeTknOD9ntsor1UlC9b2eQ4vVqs7bSA022qIJk6MPzDxQaaRdp5zzpSwn9JR0ng00B0891FQh'); // Actual Stripe Key

const PaymentForm = ({ onSuccess, userId, setError, selectedPlan = 'pack5' }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [cardComplete, setCardComplete] = useState(false);
  const [addressComplete, setAddressComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);

  const isSingle = selectedPlan === 'single';
  const amount = isSingle ? 3900 : 7900;
  const creditsToGrant = isSingle ? 1 : 5;
  const planLabel = isSingle ? 'Single Property Report' : '5-Report Pack';

  useEffect(() => {
    if (!stripe || !elements) return;

    console.log('PaymentForm mounted. Stripe loaded:', !!stripe, 'Elements loaded:', !!elements, 'userId:', userId, 'plan:', selectedPlan);

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: planLabel, amount },
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result) => {
      if (result) setPaymentRequest(pr);
    });

    pr.on('paymentmethod', async (ev) => {
      try {
        const response = await fetch('https://paws2tdfrk.execute-api.us-east-1.amazonaws.com/prod/payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount,
            userId,
            metadata: { userId, type: isSingle ? 'homeowner_single' : 'homeowner_5pack', creditsToGrant }
          }),
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to create payment intent: ${errorText}`);
        }
        const { clientSecret } = await response.json();

        const { paymentIntent, error } = await stripe.confirmPayment({
          clientSecret,
          payment_method: ev.paymentMethod.id,
          confirmation_method: 'manual',
        });

        if (error) {
          ev.complete('fail');
          setError(error.message);
        } else if (paymentIntent.status === 'succeeded') {
          ev.complete('success');
          console.log('Payment succeeded for:', userId, 'amount:', paymentIntent.amount);
          onSuccess();
          navigate('/dashboard');
        }
      } catch (err) {
        ev.complete('fail');
        setError('Payment failed: ' + err.message);
      }
    });
  }, [stripe, elements, userId, onSuccess, setError, navigate, amount, creditsToGrant, isSingle, planLabel, selectedPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !cardComplete || !addressComplete || processing) {
      setError('Please complete both card and billing address details.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const addressElement = elements.getElement('address');
      const { value: addressValue } = await addressElement.getValue();

      console.log('Submitting payment for user:', userId, 'amount:', amount);

      const response = await fetch('https://paws2tdfrk.execute-api.us-east-1.amazonaws.com/prod/payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          userId,
          metadata: { userId, type: isSingle ? 'homeowner_single' : 'homeowner_5pack', creditsToGrant }
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create payment intent: ${errorText}`);
      }
      const { clientSecret } = await response.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            email: userId,
            address: {
              line1: addressValue.address.line1,
              line2: addressValue.address.line2 || '',
              city: addressValue.address.city,
              state: addressValue.address.state,
              postal_code: addressValue.address.postal_code,
              country: addressValue.address.country,
            },
          },
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else if (result.paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded for:', userId, 'amount:', result.paymentIntent.amount);
        onSuccess();
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Payment failed: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) setError(event.error.message);
    else setError(null);
  };

  const handleAddressChange = (event) => {
    setAddressComplete(event.complete);
    if (event.error) setError(event.error.message);
    else setError(null);
  };

  return (
    <form onSubmit={handleSubmit}>
      {paymentRequest && (
        <div style={{ marginBottom: '20px' }}>
          <PaymentRequestButtonElement options={{ paymentRequest }} className="payment-request-button" />
        </div>
      )}
      <div className="StripeElement" style={{ marginBottom: '20px' }}>
        <AddressElement
          options={{
            mode: 'billing',
            fields: {
              line1: 'always',
              line2: 'auto',
              city: 'always',
              state: 'always',
              postal_code: 'always',
              country: 'always',
              phone: 'never',
            },
            validation: {
              line1: { required: 'always' },
              city: { required: 'always' },
              state: { required: 'always' },
              postal_code: { required: 'always' },
              country: { required: 'always' },
            },
          }}
          onChange={handleAddressChange}
        />
      </div>
      <div className="StripeElement" style={{ marginBottom: '25px', padding: '12px 15px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <CardElement 
          options={{ 
            style: { 
              base: { fontSize: '16px', color: '#1e293b', '::placeholder': { color: '#94a3b8' }, lineHeight: '40px' },
              invalid: { color: '#e11d48' },
            },
            hidePostalCode: true,
            disableLink: true,
          }}
          onChange={handleCardChange}
        />
      </div>
      <button 
        type="submit" 
        disabled={!stripe || !cardComplete || !addressComplete || processing}
        style={{
          width: '100%',
          backgroundColor: '#0ea5e9',
          color: 'white',
          border: 'none',
          padding: '14px 20px',
          borderRadius: '9999px',
          fontSize: '16px',
          fontWeight: '750',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: '0 4px 6px -1px rgba(14, 165, 233, 0.25)',
          opacity: (!stripe || !cardComplete || !addressComplete || processing) ? 0.6 : 1,
        }}
        onMouseOver={(e) => { if (stripe && cardComplete && addressComplete && !processing) e.target.style.backgroundColor = '#0284c7'; }}
        onMouseOut={(e) => { if (stripe && cardComplete && addressComplete && !processing) e.target.style.backgroundColor = '#0ea5e9'; }}
      >
        {processing ? 'Processing...' : (isSingle ? 'Pay $39.00 — 1 Report Credit' : 'Pay $79.00 — 5 Report Credits')}
      </button>
    </form>
  );
};

export default function WrappedPaymentForm(props) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
};